/**
 * MeetingDetailDialog Component
 * Shows full meeting details with participants list
 * Video → redirects to Communications hub with context
 * Phone → tel: link + redirect to Communications
 * Shows transcription section for completed meetings
 */

import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, Video, Phone, MapPin, User, Building2, Mail, 
  ExternalLink, PhoneCall, Copy, Check, Link2, Users, MessageSquare,
  FileText, Mic
} from 'lucide-react';
import { useState } from 'react';
import { Meeting, MeetingContact } from '@/types/calendar.types';
import { 
  MEETING_STATUS_LABELS, MEETING_FORMAT_LABELS, CONTACT_ROLE_LABELS, MEETING_TYPE_LABELS 
} from '@/constants/calendar.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

interface MeetingDetailDialogProps {
  meeting: Meeting | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  confirmed: 'bg-green-500/10 text-green-500 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
  completed: 'bg-muted text-muted-foreground border-border',
  rescheduled: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

function ContactCard({ contact, lang, onCopy, copied }: {
  contact: MeetingContact;
  lang: 'en' | 'fr';
  onCopy: (text: string, label: string) => void;
  copied: string | null;
}) {
  return (
    <div className="grid gap-1.5 text-sm bg-muted/50 rounded-lg p-3">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium">
          {contact.firstName} {contact.lastName}
        </span>
        <Badge variant="secondary" className="text-xs">
          {CONTACT_ROLE_LABELS[contact.role]?.[lang] || contact.role}
        </Badge>
      </div>

      {contact.company && (
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <span>{contact.company}</span>
        </div>
      )}

      {contact.phone && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <a href={`tel:${contact.phone}`} className="hover:underline text-primary">
              {contact.phone}
            </a>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onCopy(contact.phone!, `phone-${contact.firstName}`)}
          >
            {copied === `phone-${contact.firstName}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>
      )}

      {contact.email && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <a href={`mailto:${contact.email}`} className="hover:underline text-primary">
              {contact.email}
            </a>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onCopy(contact.email!, `email-${contact.firstName}`)}
          >
            {copied === `email-${contact.firstName}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          </Button>
        </div>
      )}
    </div>
  );
}

export function MeetingDetailDialog({ meeting, open, onOpenChange }: MeetingDetailDialogProps) {
  const { language } = useLanguage();
  const lang = language === 'fr' ? 'fr' : 'en';
  const { toast } = useToast();
  const navigate = useNavigate();
  const [copied, setCopied] = useState<string | null>(null);

  if (!meeting) return null;

  const meetingFormat = meeting.format || 'video';
  const FormatIcon = meetingFormat === 'video' ? Video : meetingFormat === 'phone' ? Phone : MapPin;
  const isActive = meeting.status !== 'cancelled' && meeting.status !== 'completed';

  // Build contacts list: prefer contacts array, fallback to single contact
  const allContacts: MeetingContact[] = meeting.contacts?.length
    ? meeting.contacts
    : meeting.contact
      ? [meeting.contact]
      : [];

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
    toast({ title: lang === 'fr' ? 'Copié!' : 'Copied!' });
  };

  // Navigate to Communications hub with meeting context
  const openInCommunications = () => {
    const getContactPhone = (contact?: MeetingContact | null) => {
      if (!contact) return '';
      const fallbackContact = contact as MeetingContact & {
        phoneNumber?: string;
        phone_number?: string;
        mobile?: string;
      };
      return fallbackContact.phone || fallbackContact.phoneNumber || fallbackContact.phone_number || fallbackContact.mobile || '';
    };

    const primaryContact = allContacts[0];
    const primaryPhone = getContactPhone(primaryContact);

    const params = new URLSearchParams();
    if (primaryContact?.firstName) params.set('name', `${primaryContact.firstName} ${primaryContact.lastName}`);
    if (primaryPhone) params.set('phone', primaryPhone);
    if (primaryContact?.email) params.set('email', primaryContact.email || '');
    params.set('meetingId', meeting.id);
    params.set('meetingTitle', meeting.title);
    if (meeting.meetingUrl) params.set('meetingUrl', meeting.meetingUrl);
    if (meetingFormat) params.set('format', meetingFormat);

    // Add all participants as JSON
    if (allContacts.length > 0) {
      params.set('participants', JSON.stringify(allContacts.map(c => ({
        name: `${c.firstName} ${c.lastName}`,
        role: c.role,
        phone: getContactPhone(c),
        email: c.email,
      }))));
    }

    navigate(`/staff/communications?${params.toString()}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg">{meeting.title}</DialogTitle>
            <Badge variant="outline" className={statusColors[meeting.status] || ''}>
              {MEETING_STATUS_LABELS[meeting.status]?.[lang] || meeting.status}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Format & Type */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <FormatIcon className="w-4 h-4 text-primary" />
              <span className="font-medium">{MEETING_FORMAT_LABELS[meetingFormat]?.[lang]}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {MEETING_TYPE_LABELS[meeting.type]?.[lang]}
            </div>
          </div>

          {/* Date, Time, Duration */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>{format(new Date(meeting.date + 'T12:00:00'), 'PPP')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{meeting.time} ({meeting.duration} min)</span>
            </div>
          </div>

          <Separator />

          {/* ═══ VIDEO CONFERENCE — REDIRECT TO COMMUNICATIONS ═══ */}
          {meetingFormat === 'video' && isActive && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                <h4 className="font-semibold text-sm text-foreground">
                  {lang === 'fr' ? 'Visioconférence' : 'Video Conference'}
                </h4>
                <Badge variant="secondary" className="text-xs">
                  {allContacts.length} {lang === 'fr' ? 'participant(s)' : 'participant(s)'}
                </Badge>
              </div>
              
              {meeting.meetingUrl && (
                <div className="flex items-center gap-2 bg-background rounded-md px-3 py-2 border">
                  <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground truncate flex-1">
                    {meeting.meetingUrl}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleCopy(meeting.meetingUrl!, 'meetingUrl')}
                  >
                    {copied === 'meetingUrl' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              )}

              <Button
                className="w-full gap-2"
                onClick={openInCommunications}
              >
                <MessageSquare className="w-4 h-4" />
                {lang === 'fr' ? 'Ouvrir dans Communications' : 'Open in Communications'}
              </Button>
            </div>
          )}

          {/* ═══ PHONE CALL — REDIRECT TO COMMUNICATIONS ═══ */}
          {meetingFormat === 'phone' && isActive && (
            <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-sm text-foreground">
                  {lang === 'fr' ? 'Appel téléphonique' : 'Phone Call'}
                </h4>
              </div>
              
              {allContacts[0]?.phone && (
                <div className="flex items-center gap-2 bg-background rounded-md px-3 py-2 border">
                  <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-mono flex-1">{allContacts[0].phone}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => handleCopy(allContacts[0].phone!, 'phone-action')}
                  >
                    {copied === 'phone-action' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              )}

              <Button
                className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white"
                onClick={openInCommunications}
              >
                <PhoneCall className="w-4 h-4" />
                {lang === 'fr' ? 'Ouvrir dans Communications' : 'Open in Communications'}
              </Button>
            </div>
          )}

          {/* ═══ PARTICIPANTS LIST ═══ */}
          {allContacts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold text-foreground">
                  {lang === 'fr' ? `Participants (${allContacts.length})` : `Participants (${allContacts.length})`}
                </h4>
              </div>
              <div className="space-y-2">
                {allContacts.map((contact, idx) => (
                  <ContactCard
                    key={idx}
                    contact={contact}
                    lang={lang}
                    onCopy={handleCopy}
                    copied={copied}
                  />
                ))}
              </div>
            </div>
          )}

          {/* External Address */}
          {meeting.format === 'in_person' && meeting.location === 'external' && meeting.externalAddress && (
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">{lang === 'fr' ? 'Adresse' : 'Address'}</h4>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{meeting.externalAddress}</span>
              </div>
            </div>
          )}

          {/* ═══ TRANSCRIPTION SECTION ═══ */}
          {(meeting.status === 'completed' || meeting.status === 'confirmed') && (
            <div className="space-y-2">
              <Separator />
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold text-foreground">
                  {lang === 'fr' ? 'Transcription & Enregistrement' : 'Transcription & Recording'}
                </h4>
              </div>
              {meeting.status === 'completed' ? (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {lang === 'fr' 
                      ? 'La transcription sera disponible dans la section Communications après la fin de l\'appel. Tous les membres de l\'équipe pourront consulter l\'historique et le résumé de la conversation.'
                      : 'The transcription will be available in the Communications section after the call ends. All team members will be able to review the history and conversation summary.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={openInCommunications}
                  >
                    <FileText className="w-4 h-4" />
                    {lang === 'fr' ? 'Voir dans Communications' : 'View in Communications'}
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {lang === 'fr' 
                      ? '🎙️ L\'enregistrement et la transcription seront activés automatiquement lors de la conférence pour assurer le contrôle qualité et le suivi.'
                      : '🎙️ Recording and transcription will be automatically enabled during the conference for quality control and follow-up.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {meeting.description && (
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">{lang === 'fr' ? 'Notes' : 'Notes'}</h4>
              <p className="text-sm text-muted-foreground">{meeting.description}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
